import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HeatmapLayerStyle, ZoomScales } from '@alltrails/maps/components/PolylineHeatmapLayer/PolylineHeatmapLayer';
import { DEFAULT_HEATMAP_STYLE } from '@alltrails/maps/components/PolylineHeatmapLayer/PolylineHeatmapLayer';
import useMap from '@alltrails/maps/hooks/useMap';
import styles from './HeatmapStyleControls.module.scss';

type SliderConfig = {
  label: string;
  key: keyof HeatmapLayerStyle;
  min: number;
  max: number;
  step: number;
  type: 'range';
};

type ColorConfig = {
  label: string;
  key: keyof HeatmapLayerStyle;
  type: 'color';
};

type FieldConfig = SliderConfig | ColorConfig;

const GLOW_FIELDS: FieldConfig[] = [
  { label: 'Color', key: 'glowColor', type: 'color' },
  { label: 'Width', key: 'glowWidth', type: 'range', min: 1, max: 40, step: 1 },
  { label: 'Opacity', key: 'glowOpacity', type: 'range', min: 0, max: 1, step: 0.05 },
  { label: 'Blur', key: 'glowBlur', type: 'range', min: 0, max: 40, step: 1 }
];

const CORE_FIELDS: FieldConfig[] = [
  { label: 'Color', key: 'coreColor', type: 'color' },
  { label: 'Width', key: 'coreWidth', type: 'range', min: 1, max: 20, step: 0.5 },
  { label: 'Opacity', key: 'coreOpacity', type: 'range', min: 0, max: 1, step: 0.05 },
  { label: 'Blur', key: 'coreBlur', type: 'range', min: 0, max: 20, step: 0.5 }
];

const SOURCE_FIELDS: FieldConfig[] = [
  { label: 'Tolerance', key: 'tolerance', type: 'range', min: 0, max: 1, step: 0.025 },
  { label: 'Buffer', key: 'buffer', type: 'range', min: 0, max: 512, step: 16 }
];

type ZoomScaleKey = 'glowWidthZoom' | 'glowBlurZoom' | 'glowOpacityZoom' | 'coreWidthZoom' | 'coreBlurZoom' | 'coreOpacityZoom';

const ZOOM_SCALE_FIELDS: { label: string; key: ZoomScaleKey }[] = [
  { label: 'Glow width', key: 'glowWidthZoom' },
  { label: 'Glow blur', key: 'glowBlurZoom' },
  { label: 'Glow opacity', key: 'glowOpacityZoom' },
  { label: 'Core width', key: 'coreWidthZoom' },
  { label: 'Core blur', key: 'coreBlurZoom' },
  { label: 'Core opacity', key: 'coreOpacityZoom' }
];

const ZOOM_STOPS: (keyof ZoomScales)[] = ['z0', 'z3', 'z6', 'z12', 'z16'];
const ZOOM_STOP_VALUES: Record<keyof ZoomScales, number> = { z0: 0, z3: 3, z6: 6, z12: 12, z16: 16 };

function nearestZoomStop(zoom: number): keyof ZoomScales {
  let best: keyof ZoomScales = 'z0';
  let bestDist = Infinity;
  for (const stop of ZOOM_STOPS) {
    const dist = Math.abs(zoom - ZOOM_STOP_VALUES[stop]);
    if (dist < bestDist) {
      bestDist = dist;
      best = stop;
    }
  }
  return best;
}

function rgbToHex(rgb: string): string {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) {
    return rgb;
  }
  const [r, g, b] = match.map(Number);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function hexToRgb(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255})`;
}

function formatZoomScales(scales: ZoomScales): string {
  return `{ z0: ${scales.z0}, z3: ${scales.z3}, z6: ${scales.z6}, z12: ${scales.z12}, z16: ${scales.z16} }`;
}

const round = (n: number) => Math.round(n * 1000) / 1000;

const SPEC_ZOOM_STOPS: { zoom: number; key: keyof ZoomScales }[] = [
  { zoom: 0, key: 'z0' },
  { zoom: 3, key: 'z3' },
  { zoom: 6, key: 'z6' },
  { zoom: 12, key: 'z12' },
  { zoom: 16, key: 'z16' }
];

function formatInterp(base: number, scales: ZoomScales, propIndent: string, clampOne = false): string {
  const itemIndent = `${propIndent}  `;
  const lines = SPEC_ZOOM_STOPS.map(({ zoom, key }) => {
    const raw = base * scales[key];
    const value = round(clampOne ? Math.min(raw, 1) : raw);
    return `${itemIndent}${zoom}, ${value}`;
  }).join(',\n');
  return `[\n${itemIndent}"interpolate", ["linear"], ["zoom"],\n${lines}\n${propIndent}]`;
}

function formatLayer(
  id: string,
  color: string,
  width: number,
  widthZoom: ZoomScales,
  opacity: number,
  opacityZoom: ZoomScales,
  blur: number,
  blurZoom: ZoomScales
): string {
  const paintIndent = '        ';
  return `    {
      "id": "${id}",
      "type": "line",
      "source": "polyline-heatmap-source",
      "layout": { "line-cap": "round", "line-join": "round" },
      "paint": {
        "line-color": "${color}",
        "line-width": ${formatInterp(width, widthZoom, paintIndent)},
        "line-opacity": ${formatInterp(opacity, opacityZoom, paintIndent, true)},
        "line-blur": ${formatInterp(blur, blurZoom, paintIndent)}
      }
    }`;
}

function formatStyleAsMapboxSpec(s: HeatmapLayerStyle): string {
  const glow = formatLayer(
    'polyline-heatmap-glow',
    s.glowColor,
    s.glowWidth,
    s.glowWidthZoom,
    s.glowOpacity,
    s.glowOpacityZoom,
    s.glowBlur,
    s.glowBlurZoom
  );
  const core = formatLayer(
    'polyline-heatmap',
    s.coreColor,
    s.coreWidth,
    s.coreWidthZoom,
    s.coreOpacity,
    s.coreOpacityZoom,
    s.coreBlur,
    s.coreBlurZoom
  );
  return `{
  "sources": {
    "polyline-heatmap-source": {
      "type": "geojson",
      "data": "<your geojson FeatureCollection>",
      "tolerance": ${s.tolerance},
      "buffer": ${s.buffer}
    }
  },
  "layers": [
${glow},
${core}
  ]
}`;
}

function formatStyleAsObject(s: HeatmapLayerStyle): string {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(s)) {
    if (typeof v === 'string') {
      lines.push(`  ${k}: '${v}',`);
    } else if (typeof v === 'object' && v !== null) {
      lines.push(`  ${k}: ${formatZoomScales(v as ZoomScales)},`);
    } else {
      lines.push(`  ${k}: ${v},`);
    }
  }
  return `const heatmapStyle: HeatmapLayerStyle = {\n${lines.join('\n')}\n};`;
}

function parseStyleObject(text: string): HeatmapLayerStyle | null {
  try {
    const result: Record<string, unknown> = {};
    const propRegex = /(\w+)\s*:\s*(?:'([^']*)'|(\{[^}]+\})|([\d.]+))\s*,?/g;
    let match;

    while ((match = propRegex.exec(text)) !== null) {
      const key = match[1];
      if (match[2] !== undefined) {
        result[key] = match[2];
      } else if (match[3]) {
        const obj: Record<string, number> = {};
        const kvRegex = /(\w+)\s*:\s*([\d.]+)/g;
        let kv;
        while ((kv = kvRegex.exec(match[3])) !== null) {
          obj[kv[1]] = Number(kv[2]);
        }
        result[key] = obj;
      } else if (match[4] !== undefined) {
        result[key] = Number(match[4]);
      }
    }

    const required = [
      'glowColor',
      'glowWidth',
      'glowOpacity',
      'glowBlur',
      'coreColor',
      'coreWidth',
      'coreOpacity',
      'coreBlur',
      'glowWidthZoom',
      'glowBlurZoom',
      'glowOpacityZoom',
      'coreWidthZoom',
      'coreBlurZoom',
      'coreOpacityZoom',
      'tolerance',
      'buffer'
    ];

    for (const key of required) {
      if (!(key in result)) {
        return null;
      }
    }

    return result as unknown as HeatmapLayerStyle;
  } catch {
    return null;
  }
}

type StylePreset = {
  name: string;
  glowColor: string;
  coreColor: string;
  style: HeatmapLayerStyle;
};

const STYLE_PRESETS: StylePreset[] = [
  {
    name: 'Classic',
    glowColor: 'rgb(255, 220, 0)',
    coreColor: 'rgb(255, 40, 0)',
    style: { ...DEFAULT_HEATMAP_STYLE }
  },
  {
    name: 'Blaze',
    glowColor: 'rgb(255, 100, 0)',
    coreColor: 'rgb(255, 240, 50)',
    style: {
      glowColor: 'rgb(255, 100, 0)',
      glowWidth: 20,
      glowOpacity: 0.45,
      glowBlur: 18,
      coreColor: 'rgb(255, 240, 50)',
      coreWidth: 5,
      coreOpacity: 0.95,
      coreBlur: 1,
      glowWidthZoom: { z0: 3, z3: 2.2, z6: 1.2, z12: 0.8, z16: 1 },
      glowBlurZoom: { z0: 3, z3: 2, z6: 1.2, z12: 0.8, z16: 1 },
      glowOpacityZoom: { z0: 2.2, z3: 1.65, z6: 1.3, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3.5, z3: 2.5, z6: 1.2, z12: 1, z16: 1.2 },
      coreBlurZoom: { z0: 4, z3: 3, z6: 1, z12: 1, z16: 1 },
      coreOpacityZoom: { z0: 1.1, z3: 1.05, z6: 1.02, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  },
  {
    name: 'Electric',
    glowColor: 'rgb(0, 255, 200)',
    coreColor: 'rgb(130, 255, 50)',
    style: {
      glowColor: 'rgb(0, 255, 200)',
      glowWidth: 18,
      glowOpacity: 0.2,
      glowBlur: 20,
      coreColor: 'rgb(130, 255, 50)',
      coreWidth: 2,
      coreOpacity: 1,
      coreBlur: 0,
      glowWidthZoom: { z0: 2.5, z3: 1.8, z6: 1, z12: 0.7, z16: 1 },
      glowBlurZoom: { z0: 3, z3: 2, z6: 1.2, z12: 0.8, z16: 0.8 },
      glowOpacityZoom: { z0: 3, z3: 2.25, z6: 1.5, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3, z3: 2, z6: 1, z12: 1, z16: 1.5 },
      coreBlurZoom: { z0: 1.5, z3: 1, z6: 1, z12: 1, z16: 1 },
      coreOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  },
  {
    name: 'Plasma',
    glowColor: 'rgb(255, 0, 180)',
    coreColor: 'rgb(255, 255, 100)',
    style: {
      glowColor: 'rgb(255, 0, 180)',
      glowWidth: 24,
      glowOpacity: 0.3,
      glowBlur: 24,
      coreColor: 'rgb(255, 255, 100)',
      coreWidth: 2,
      coreOpacity: 1,
      coreBlur: 0,
      glowWidthZoom: { z0: 3, z3: 2, z6: 1.2, z12: 0.7, z16: 0.9 },
      glowBlurZoom: { z0: 3, z3: 2.2, z6: 1.4, z12: 0.8, z16: 0.8 },
      glowOpacityZoom: { z0: 2.5, z3: 2, z6: 1.4, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3.5, z3: 2.5, z6: 1, z12: 1, z16: 1.5 },
      coreBlurZoom: { z0: 1.5, z3: 1, z6: 1, z12: 1, z16: 1 },
      coreOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  },
  {
    name: 'Aurora',
    glowColor: 'rgb(140, 0, 255)',
    coreColor: 'rgb(255, 50, 200)',
    style: {
      glowColor: 'rgb(140, 0, 255)',
      glowWidth: 22,
      glowOpacity: 0.25,
      glowBlur: 22,
      coreColor: 'rgb(255, 50, 200)',
      coreWidth: 3,
      coreOpacity: 0.75,
      coreBlur: 3,
      glowWidthZoom: { z0: 2.5, z3: 1.8, z6: 1, z12: 0.6, z16: 0.8 },
      glowBlurZoom: { z0: 2.5, z3: 1.8, z6: 1, z12: 0.7, z16: 0.8 },
      glowOpacityZoom: { z0: 2.5, z3: 1.8, z6: 1.4, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3, z3: 2, z6: 1, z12: 1, z16: 1.5 },
      coreBlurZoom: { z0: 3, z3: 2, z6: 1, z12: 0.8, z16: 1 },
      coreOpacityZoom: { z0: 1.3, z3: 1.2, z6: 1.1, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  },
  {
    name: 'Thermal',
    glowColor: 'rgb(180, 0, 0)',
    coreColor: 'rgb(255, 255, 200)',
    style: {
      glowColor: 'rgb(180, 0, 0)',
      glowWidth: 16,
      glowOpacity: 0.4,
      glowBlur: 14,
      coreColor: 'rgb(255, 255, 200)',
      coreWidth: 3,
      coreOpacity: 0.9,
      coreBlur: 1,
      glowWidthZoom: { z0: 3, z3: 2, z6: 1.2, z12: 0.8, z16: 1 },
      glowBlurZoom: { z0: 3, z3: 2, z6: 1.2, z12: 0.8, z16: 1 },
      glowOpacityZoom: { z0: 2, z3: 1.6, z6: 1.3, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3.5, z3: 2.5, z6: 1, z12: 1, z16: 1.5 },
      coreBlurZoom: { z0: 3, z3: 2, z6: 1, z12: 1, z16: 1 },
      coreOpacityZoom: { z0: 1.2, z3: 1.1, z6: 1.05, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  },
  {
    name: 'Midnight',
    glowColor: 'rgb(0, 80, 255)',
    coreColor: 'rgb(150, 200, 255)',
    style: {
      glowColor: 'rgb(0, 80, 255)',
      glowWidth: 14,
      glowOpacity: 0.35,
      glowBlur: 16,
      coreColor: 'rgb(150, 200, 255)',
      coreWidth: 2.5,
      coreOpacity: 0.85,
      coreBlur: 1,
      glowWidthZoom: { z0: 2.5, z3: 1.8, z6: 1, z12: 0.8, z16: 1 },
      glowBlurZoom: { z0: 2.5, z3: 1.8, z6: 1.2, z12: 0.8, z16: 0.8 },
      glowOpacityZoom: { z0: 2, z3: 1.55, z6: 1.25, z12: 1, z16: 1 },
      coreWidthZoom: { z0: 3, z3: 2, z6: 1, z12: 1, z16: 1.5 },
      coreBlurZoom: { z0: 3.5, z3: 2.5, z6: 1, z12: 1, z16: 1 },
      coreOpacityZoom: { z0: 1.2, z3: 1.1, z6: 1.05, z12: 1, z16: 1 },
      tolerance: 0.375,
      buffer: 128
    }
  }
];

type Props = {
  style: HeatmapLayerStyle;
  onChange: (style: HeatmapLayerStyle) => void;
};

const HeatmapStyleControls = ({ style, onChange }: Props) => {
  const [showZoom, setShowZoom] = useState(false);
  const [codeText, setCodeText] = useState(() => formatStyleAsObject(style));
  const [codeError, setCodeError] = useState(false);
  const isEditingCode = useRef(false);
  const map = useMap();
  const [activeStop, setActiveStop] = useState<keyof ZoomScales>('z6');

  useEffect(() => {
    const m = map?.getMap();
    if (!m) {
      return;
    }
    const update = () => setActiveStop(nearestZoomStop(m.getZoom()));
    update();
    m.on('zoom', update);
    return () => {
      m.off('zoom', update);
    };
  }, [map]);

  useEffect(() => {
    if (!isEditingCode.current) {
      setCodeText(formatStyleAsObject(style));
      setCodeError(false);
    }
  }, [style]);

  const handleChange = useCallback(
    (key: keyof HeatmapLayerStyle, rawValue: string, isColor: boolean) => {
      const value = isColor ? hexToRgb(rawValue) : Number(rawValue);
      onChange({ ...style, [key]: value });
    },
    [style, onChange]
  );

  const handleZoomScaleChange = useCallback(
    (scaleKey: ZoomScaleKey, stop: keyof ZoomScales, rawValue: string) => {
      const current = style[scaleKey];
      onChange({ ...style, [scaleKey]: { ...current, [stop]: Number(rawValue) } });
    },
    [style, onChange]
  );

  const handleCodeChange = useCallback(
    (newText: string) => {
      isEditingCode.current = true;
      setCodeText(newText);
      const parsed = parseStyleObject(newText);
      if (parsed) {
        setCodeError(false);
        onChange(parsed);
      } else {
        setCodeError(true);
      }
      requestAnimationFrame(() => {
        isEditingCode.current = false;
      });
    },
    [onChange]
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codeText);
    } catch {
      // clipboard not available
    }
  }, [codeText]);

  const mapboxSpecText = useMemo(() => formatStyleAsMapboxSpec(style), [style]);

  const handleCopyMapboxSpec = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(mapboxSpecText);
    } catch {
      // clipboard not available
    }
  }, [mapboxSpecText]);

  const handleReset = useCallback(() => {
    onChange({ ...DEFAULT_HEATMAP_STYLE });
  }, [onChange]);

  const renderFields = (fields: FieldConfig[]) =>
    fields.map(field => {
      const value = style[field.key];

      if (field.type === 'color') {
        return (
          <label key={field.key} className={styles.field}>
            <span className={styles.label}>{field.label}</span>
            <input
              type="color"
              aria-label={field.label}
              value={rgbToHex(value as string)}
              onChange={e => handleChange(field.key, e.target.value, true)}
            />
          </label>
        );
      }

      return (
        <label key={field.key} className={styles.field}>
          <span className={styles.label}>{field.label}</span>
          <input
            type="range"
            aria-label={field.label}
            min={field.min}
            max={field.max}
            step={field.step}
            value={value as number}
            onChange={e => handleChange(field.key, e.target.value, false)}
          />
          <span className={styles.value}>{value as number}</span>
        </label>
      );
    });

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <strong>Style editor</strong>
        <button type="button" onClick={handleReset} className={styles.resetBtn}>
          Reset
        </button>
      </div>

      <div className={styles.presets}>
        {STYLE_PRESETS.map(preset => (
          <button key={preset.name} type="button" className={styles.presetBtn} onClick={() => onChange({ ...preset.style })}>
            <span
              className={styles.presetSwatch}
              style={{ background: `linear-gradient(135deg, ${preset.glowColor} 0%, ${preset.coreColor} 100%)` }}
            />
            <span className={styles.presetName}>{preset.name}</span>
          </button>
        ))}
      </div>

      <span className={styles.sectionLabel}>Glow layer</span>
      <div className={styles.fields}>{renderFields(GLOW_FIELDS)}</div>

      <span className={styles.sectionLabel}>Core layer</span>
      <div className={styles.fields}>{renderFields(CORE_FIELDS)}</div>

      <span className={styles.sectionLabel}>Source</span>
      <div className={styles.fields}>{renderFields(SOURCE_FIELDS)}</div>

      <div className={styles.zoomSection}>
        <button type="button" className={styles.zoomToggle} onClick={() => setShowZoom(prev => !prev)}>
          {showZoom ? '▾' : '▸'} Zoom scaling
        </button>

        {showZoom && (
          <div className={styles.zoomFields}>
            <div className={styles.zoomHeader}>
              <span />
              {ZOOM_STOPS.map(stop => (
                <span key={stop} className={stop === activeStop ? styles.activeZoom : undefined}>
                  {stop}
                </span>
              ))}
            </div>

            {ZOOM_SCALE_FIELDS.map(({ label, key }) => (
              <div key={key} className={styles.zoomRow}>
                <span className={styles.label}>{label}</span>
                {ZOOM_STOPS.map(stop => (
                  <input
                    key={stop}
                    type="number"
                    aria-label={`${label} ${stop}`}
                    className={`${styles.zoomInput} ${stop === activeStop ? styles.activeZoomInput : ''}`}
                    value={style[key][stop]}
                    min={0}
                    max={10}
                    step={0.1}
                    onChange={e => handleZoomScaleChange(key, stop, e.target.value)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.output}>
        <div className={styles.outputHeader}>
          <span className={styles.outputLabel}>Code</span>
          <button type="button" onClick={handleCopy} className={styles.copyBtn}>
            Copy
          </button>
        </div>
        <textarea
          aria-label="Code"
          className={`${styles.codeEditor} ${codeError ? styles.codeEditorError : ''}`}
          value={codeText}
          onChange={e => handleCodeChange(e.target.value)}
          spellCheck={false}
        />
      </div>

      <div className={styles.output}>
        <div className={styles.outputHeader}>
          <span className={styles.outputLabel}>Mapbox style spec (iOS / Android)</span>
          <button type="button" onClick={handleCopyMapboxSpec} className={styles.copyBtn}>
            Copy
          </button>
        </div>
        <textarea
          aria-label="Mapbox style spec"
          className={`${styles.codeEditor} ${styles.codeEditorReadOnly}`}
          value={mapboxSpecText}
          readOnly
          spellCheck={false}
        />
      </div>
    </div>
  );
};

export default HeatmapStyleControls;
